import { useDisclosure } from '@nextui-org/react'
import { useState } from 'react'

import { useMutation, useQueryClient } from '@tanstack/react-query'
import { AlertCircle, Lock, Mail, User } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '~/components/common/Button'
import { ConfirmationModal } from '~/components/modals/ConfirmationModal'
import { SettingsSection } from '~/components/common/SettingsSection'
import { WarningIcon } from '~/constants/icon'
import { useUser } from '~/hooks'
import { I18nKey } from '~/i18n/declaration'
import { QueryKeys } from '~/services/queryClient'
import { deleteAccount, updateUserProfile } from '~/services/userService'
import { BetterToast } from '~/utils/toast'
import { ChangePasswordModal } from './components/user/ChangePasswordModal'
import { SessionsSection } from './components/user/SessionsSection'
import styles from './settings.module.scss'
import { cm } from '~/utils/utils'

export default function UserSettingsTab() {
  const queryClient = useQueryClient()
  const user = useUser()
  const { mutate: updateUser } = useMutation({
    mutationFn: updateUserProfile,
    onSuccess() {
      queryClient.invalidateQueries({ queryKey: QueryKeys.User })
    },
    onError(error) {
      BetterToast.error(error.message)
    }
  })
  const [userName, setUserName] = useState(user.name)
  const { t } = useTranslation()

  const {
    isOpen: changePwdModalVisible,
    onOpen: onChangePwdModalOpen,
    onOpenChange: onChangePwdModalOpenChange
  } = useDisclosure()

  const {
    isOpen: deleteAccountModalVisible,
    onOpen: onDeleteAccountModalOpen,
    onOpenChange: onDeleteAccountModalOpenChange
  } = useDisclosure()

  const [isEditingName, setIsEditingName] = useState(false)

  const handleNameBlur = () => {
    const trimmed = userName.trim()
    if (trimmed === user.name || trimmed.length === 0) {
      setUserName(user.name)
      setIsEditingName(false)
      return
    }
    const onSuccess = () => {
      BetterToast.success('Name updated')
      setIsEditingName(false)
    }
    updateUser({ name: trimmed }, { onSuccess })
  }

  const onSubmitChangePassword = (oldPassword: string, newPassword: string) => {
    const onSuccess = () => {
      BetterToast.success('Password updated')
      onChangePwdModalOpenChange()
      setTimeout(() => {
        location.href = '/api/user/signout'
      }, 1000)
    }
    updateUser({ old_password: oldPassword, password: newPassword }, { onSuccess })
  }

  const [isDisabled, setIsDisabled] = useState(false)
  const handleConfirmDeleteAccount = async () => {
    try {
      setIsDisabled(true)
      const message = await deleteAccount()
      BetterToast.success(message)
      setTimeout(() => {
        location.href = '/signin'
      }, 1000)
    } catch (error: unknown) {
      const errorMessage = error as Error
      BetterToast.error(errorMessage.message)
      setIsDisabled(false)
    }
  }

  return (
    <>
      <div className={cm('w-full h-full overflow-auto pb-16 sm:pb-32', styles['no-scroll-bar'])}>
        <div className='flex flex-col gap-6 sm:gap-9'>
          {/* User Profile */}
          <div className='flex flex-col'>
            <SettingsSection className='px-4 py-3 sm:px-6 sm:py-4'>
              {/* User Name */}
              <div className='flex flex-col gap-2 py-3 sm:py-0 sm:flex-row sm:items-center sm:justify-between border-b border-tokens-neutral-border-2'>
                <div className='flex items-center gap-4 shrink-0'>
                  <span className='flex items-center justify-center w-6 h-6 shrink-0 text-tokens-neutral-icon-solid'>
                    <User className='w-[18px] h-[18px]' />
                  </span>
                  <span className='font-sans font-medium text-[14px] leading-[22px] text-[rgba(0,0,0,0.88)] px-2 select-none'>
                    User Name
                  </span>
                </div>
                {isEditingName ? (
                  <input
                    type='text'
                    value={userName}
                    onChange={e => setUserName(e.target.value)}
                    onBlur={handleNameBlur}
                    onKeyDown={e => {
                      if (e.key === 'Enter') e.currentTarget.blur()
                      if (e.key === 'Escape') {
                        setUserName(user.name)
                        setIsEditingName(false)
                      }
                    }}
                    autoFocus
                    className='w-full sm:w-[240px] h-10 sm:h-12 py-2 px-3 ml-8 sm:ml-0 border border-tokens-status-link-1 rounded-xl text-left sm:text-right font-mono font-semibold text-[14px] sm:text-[15px] leading-normal text-[rgba(0,0,0,0.65)] overflow-hidden text-ellipsis bg-tokens-neutral-bg-white outline-none shadow-[0px_2px_4px_rgba(0,0,0,0.02),0px_1px_6px_-1px_rgba(0,0,0,0.02),0px_1px_2px_rgba(0,0,0,0.03)]'
                    aria-label='User name input'
                  />
                ) : (
                  <div className='relative group h-10 sm:h-12 flex items-center ml-8 sm:ml-0'>
                    <button
                      type='button'
                      onClick={() => setIsEditingName(true)}
                      className='py-2 px-3 font-mono font-semibold text-[14px] sm:text-[15px] leading-normal text-left sm:text-right text-[rgba(0,0,0,0.65)] overflow-hidden text-ellipsis whitespace-nowrap cursor-pointer hover:opacity-70 transition-opacity'
                    >
                      {userName}
                    </button>
                    <div className='absolute -bottom-8 right-0 px-2 py-1 bg-tokens-neutral-bg-action text-tokens-neutral-text-white text-xs rounded-md opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-10 select-none'>
                      Click to edit
                    </div>
                  </div>
                )}
              </div>

              {/* Email */}
              <div className='flex flex-col gap-1 py-3 sm:py-0 sm:flex-row sm:items-center sm:justify-between border-b border-tokens-neutral-border-2'>
                <div className='flex items-center gap-4 shrink-0'>
                  <span className='flex items-center justify-center w-6 h-6 shrink-0 text-tokens-neutral-icon-solid'>
                    <Mail className='w-[18px] h-[18px]' />
                  </span>
                  <span className='font-sans font-medium text-[14px] leading-[22px] text-[rgba(0,0,0,0.88)] px-2 select-none'>
                    Email
                  </span>
                </div>
                <span className='py-1 px-3 ml-8 sm:ml-0 sm:py-2 sm:h-12 sm:flex sm:items-center font-mono font-semibold text-[13px] sm:text-[15px] leading-normal text-left sm:text-right text-[rgba(0,0,0,0.65)] overflow-hidden text-ellipsis whitespace-nowrap'>
                  {user.email}
                </span>
              </div>

              {/* Password */}
              <div className='flex flex-col gap-3 py-3 sm:py-0 sm:flex-row sm:items-center sm:justify-between sm:h-12'>
                <div className='flex items-center gap-4 shrink-0'>
                  <span className='flex items-center justify-center w-6 h-6 shrink-0 text-tokens-neutral-icon-solid'>
                    <Lock className='w-[18px] h-[18px]' />
                  </span>
                  <span className='font-sans font-medium text-[14px] leading-[22px] text-[rgba(0,0,0,0.88)] px-2 select-none'>
                    Password
                  </span>
                </div>
                <div className='ml-8 sm:ml-0'>
                  <Button
                    variant='outlined'
                    onClick={onChangePwdModalOpen}
                    aria-label='Change password button'
                    className='select-none'
                  >
                    Change Password
                  </Button>
                </div>
              </div>
            </SettingsSection>
          </div>

          {/* Sessions */}
          <SessionsSection />

          {/* Delete My Account */}
          <div className='pt-6 sm:pt-12'>
            <div className='border-t border-tokens-neutral-border-2' />
          </div>
          <div className='flex flex-col gap-4 px-4 py-5 sm:flex-row sm:items-center sm:gap-4 sm:px-6 sm:py-8 bg-tokens-status-error-10 rounded-3xl'>
            <div className='flex items-start gap-3 sm:gap-4 flex-1 min-w-0'>
              <span className='flex items-center justify-center w-6 h-6 shrink-0 mt-0.5 sm:mt-0'>
                <AlertCircle className='w-[18px] h-[18px] text-tokens-status-error-text' />
              </span>
              <div className='flex flex-col gap-1 flex-1 min-w-0'>
                <span className='font-sans font-medium text-[14px] leading-[22px] text-tokens-status-error-text select-none'>
                  Delete My Account
                </span>
                <span className='font-sans font-medium text-[12px] leading-[16px] text-[rgba(234,70,45,0.7)] select-none'>
                  Permanently delete this account and all its data. This action cannot be undone.
                </span>
              </div>
            </div>
            <div className='flex justify-end sm:justify-start shrink-0'>
              <Button
                variant='destructive'
                onClick={onDeleteAccountModalOpen}
                aria-label='Delete account button'
                className='select-none'
              >
                Delete Account
              </Button>
            </div>
          </div>
        </div>
      </div>

      <ChangePasswordModal
        isOpen={changePwdModalVisible}
        onClose={onChangePwdModalOpenChange}
        onSubmit={onSubmitChangePassword}
      />

      <ConfirmationModal
        isOpen={deleteAccountModalVisible}
        onClose={onDeleteAccountModalOpenChange}
        title={t(I18nKey.USERFORM_DELETE_ACCOUNT_MODEL$MODAL_TITLE)}
        content={
          <div className='flex flex-col gap-4 select-none'>
            <p className='text-base text-[rgba(0,0,0,0.88)] font-medium leading-[22px] mb-4'>
              {t(I18nKey.USERFORM_DELETE_ACCOUNT_MODEL$MODAL_CONTENT)}
            </p>
          </div>
        }
        icon={<WarningIcon />}
        buttons={
          <div className='flex gap-[5px]'>
            <Button
              onClick={onDeleteAccountModalOpenChange}
              variant='outlined'
              className='select-none'
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmDeleteAccount}
              variant='destructive'
              isDisabled={isDisabled}
              aria-label='Confirm delete account button'
              className='select-none'
            >
              {t(I18nKey.USERFORM_DELETE_ACCOUNT_MODEL$DELETE_BUTTON)}
            </Button>
          </div>
        }
      />
    </>
  )
}
